import { Directive, HostListener, Input } from '@angular/core';
import { DetectDeviceService } from '../../user-agent.helpers';

@Directive({
  selector: '[appToggleOverflow]',
})
export class OverflowToggleDirective {
  @Input({ required: false }) public hasSizeLimit = false;

  public prevOverflow: string;
  public prevOverflowY: string;
  public prevWrapperOverflowY: string;
  public prevWrapperHeight: string;
  public prevWrapperMinHeight: string;
  public wrapperElement: HTMLElement | null;

  private visionFixedElementName = 'ngb-typeahead-window';
  private scrollbarWidth: string;

  @HostListener('click', ['$event'])
  public toggleOverflow(event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    this.applyOverflowStyles();
  }

  /**
   * @description This method calls several other methods to restore the previous
   * overflow state for the document and elements if they were changed
   * when the toggleOverflow() method was called.
   * @param event
   */
  @HostListener('blur', ['$event'])
  public restoreOverflow(event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    this.restoreBodyOverflow();
    if (this.hasSizeLimit && DetectDeviceService.isTouchDevice()) {
      this.restoreWrapperOverflow();
      this.restoreScrollableElementsOverflow();
    }
  }

  /**
   * @description This method sets the previous overflow state of the body of the document
   * by restoring the previous value of the overflow property.
   * @private
   */
  private restoreBodyOverflow(): void {
    document.body.style.overflow = '';
    document.documentElement.style.overflowY = '';
    document.documentElement.style.paddingRight = '0';
  }

  /**
   * @description This method restores the wrapper element's previous overflow state
   * if it was changed when the toggleOverflow() method was called.
   * @private
   */
  private restoreWrapperOverflow(): void {
    if (this.wrapperElement) {
      this.wrapperElement.style.setProperty('overflowY', this.prevWrapperOverflowY);
      this.wrapperElement.style.setProperty('height', this.prevWrapperHeight);
      this.wrapperElement.style.setProperty('minHeight', this.prevWrapperMinHeight);
    }
  }

  /**
   * @description This method restores the previous overflow state
   * for all .scroll-able elements if it was changed when the toggleOverflow() method was called.
   * @private
   */
  private restoreScrollableElementsOverflow(): void {
    const scrollableElements: NodeListOf<HTMLElement> = document.querySelectorAll('.scroll-able');
    scrollableElements.forEach((element: HTMLElement) => {
      element['style'].overflowY = '';
    });
  }

  private getScrollbarWidth(): string {
    const div: HTMLDivElement = document.createElement('div');
    div.style.width = '100px';
    div.style.height = '0';
    div.style.overflowY = 'scroll';
    document.body.appendChild(div);
    const scrollbarWidth: string = (div.offsetWidth - div.clientWidth).toString() + 'px';
    document.body.removeChild(div);
    return scrollbarWidth;
  }

  /**
   * @description Applies overflow styles to the body, html and wrapper elements based on the current context.
   * If hasSizeLimit input is true and the device is touch-enabled, it also computes and applies
   * additional styles to the wrapper element and scrollable elements, and observes for the addition
   * of a specific element in the DOM and updates its transform style.
   * @private
   */
  private applyOverflowStyles(): void {
    // Save current overflow state
    this.prevOverflow = document.body.style.overflow;
    this.prevOverflowY = document.documentElement.style.overflowY;

    if (this.hasSizeLimit && DetectDeviceService.isTouchDevice()) {
      this.wrapperElement = document.querySelector('.wrapper');

      if (this.wrapperElement) {
        this.wrapperElement['style'].overflowY = 'hidden';
        this.wrapperElement['style'].height = '99vh';
        this.wrapperElement['style'].minHeight = '99vh';

        this.prevWrapperOverflowY = this.wrapperElement['style'].overflowY;
        this.prevWrapperHeight = this.wrapperElement['style'].height;
        this.prevWrapperMinHeight = this.wrapperElement['style'].minHeight;
      }

      // Disable overflow for scrollable elements
      const scrollableElements: NodeListOf<HTMLElement> = document.querySelectorAll('.scroll-able');
      scrollableElements.forEach((element: HTMLElement) => {
        element['style'].overflowY = 'hidden';
      });

      // Compute scrollbar width and add paddingRight to html element
      this.scrollbarWidth = this.getScrollbarWidth();
      document.documentElement.style.paddingRight = this.scrollbarWidth;

      // Observe for the addition of the vision fixed element and update its transform style
      const observer: MutationObserver = new MutationObserver(
        (mutationsList: MutationRecord[], observer: MutationObserver) => {
          for (const mutation of mutationsList) {
            if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
              mutation.addedNodes.forEach((node: Node) => {
                if (node['tagName'] === this.visionFixedElementName.toUpperCase()) {
                  const element: HTMLElement | null = window.document.getElementById(node['id'] as string);
                  if (element) {
                    element.style.transform = window.getComputedStyle(element).getPropertyValue('transform');

                    window.addEventListener('scroll', () => {
                      const elementScrollable: HTMLElement | null = window.document.getElementById(
                        node['id'] as string,
                      );

                      if (elementScrollable) {
                        elementScrollable.style.transform = element.style.transform;
                      }
                    });
                  }
                  observer.disconnect();
                }
              });
            }
          }
        },
      );
      observer.observe(document.body, { childList: true, subtree: true });
    }
  }
}
